import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { Button, Input, Pill, SkeletonSet } from '@cwos/ui-admin';
import { LoadFailure } from '../LoadFailure.js';
import { usePermissions } from '../permissions.js';
import { agents } from './agentsClient.js';
import { MIN_DECISION_REASON } from './AgentApprovals.js';
import {
  AGENTS_APPROVE_KEY,
  agentAuthority,
  agentAuthoritySentence,
  authorityOpen,
} from './consentLogic.js';
import {
  actionOutcomeNote,
  actionOutcomePill,
  actionOutcomeSentence,
  isRunLive,
  runStatusLabel,
  runStatusPill,
  triggerSentence,
} from './agentsLogic.js';

/**
 * ONE RUN: what it set out to do, what it attempted, and what actually
 * changed.
 *
 * ===========================================================================
 * THE OUTCOME COLUMN IS SIX ANSWERS AND THE LAST THREE ARE DIFFERENT
 * ===========================================================================
 * `EXECUTED` is a change that happened. `FAILED` is a measured negative — the
 * executor answered with an error and nothing changed. `OUTCOME_UNKNOWN` is
 * neither: the run was abandoned mid-attempt (a process kill, a deploy) and
 * the reaper found the in-flight row afterwards, so nobody ever observed how
 * it ended and the change may or may not be sitting in the records.
 *
 * A surface that folded `OUTCOME_UNKNOWN` into "failed" would be telling an
 * operator that nothing changed, which the system does not know. Folding it
 * into "done" would be worse. It gets its own words and its own explanation,
 * because it is the one row on this page somebody has to go and check by
 * hand.
 *
 * ===========================================================================
 * TARGETS ARE COUNTED, NEVER NAMED
 * ===========================================================================
 * Each action carries the opaque ids of the records it touched. This surface
 * is gated on `agents:read` and nothing else — it does not check
 * `directory:read` — so the server sends a COUNT and not the ids. A reader
 * authorized to audit an agent is not thereby authorized to enumerate the
 * people it acted on, and the count answers the operator's real question
 * without the disclosure.
 */

export function AgentRunDetail() {
  const params = useParams({ strict: false }) as { runId?: string };
  const runId = params.runId ?? '';
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const approve = agentAuthority(permissions, AGENTS_APPROVE_KEY);
  // Cancel is `agents:pause` at the door (an org-wide `can`, like every agent verb).
  const mayCancel = authorityOpen(agentAuthority(permissions, 'agents:pause'));
  const [reason, setReason] = useState('');
  const [actionError, setActionError] = useState<unknown>(null);

  const detail = useQuery({
    queryKey: ['agents', 'run', runId],
    queryFn: () => agents.run(runId),
    enabled: runId !== '',
  });

  const decide = useMutation({
    mutationFn: ({
      approvalId,
      decision,
    }: {
      approvalId: string;
      decision: 'GRANTED' | 'DENIED';
    }) => agents.decideApproval(approvalId, decision, reason.trim()),
    onSuccess: () => {
      setActionError(null);
      setReason('');
      void queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
    onError: (err) => setActionError(err),
  });

  const cancel = useMutation({
    mutationFn: () => agents.cancelRun(runId),
    onSuccess: () => {
      setActionError(null);
      void queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
    onError: (err) => setActionError(err),
  });

  if (detail.isLoading) return <SkeletonSet surface="drawer" rows={8} />;
  if (detail.isError) {
    return (
      <LoadFailure
        error={detail.error}
        message="We could not load this run."
        onRetry={() => void detail.refetch()}
        mainRead
        refusedHeading="You cannot open this run"
      />
    );
  }

  const run = detail.data;
  if (!run) return null;

  // The run's prepared-but-uncommitted layer: actions still waiting on a
  // human decision. The glass treatment marks exactly this state and nothing
  // that has already happened.
  const pendingApproval = run.approvals.find((approval) => approval.status === 'PENDING');
  const prepared = pendingApproval !== undefined;
  const approveSentence = agentAuthoritySentence(approve, 'decide what this run is waiting on');
  const decisionBlocked = reason.trim().length < MIN_DECISION_REASON || decide.isPending;

  return (
    <article className="rn-col" style={{ gap: 'var(--space-16)' }}>
      <a className="rn-link" href={`/agents/${run.deploymentId}`}>
        ← The agent
      </a>

      <header className="rn-col" style={{ gap: 'var(--space-8)' }}>
        <div className="rn-row" style={{ gap: 'var(--space-12)', alignItems: 'center' }}>
          <h1>Run</h1>
          <Pill variant={runStatusPill(run.status)}>{runStatusLabel(run.status)}</Pill>
        </div>
        <p className="rn-muted">
          {triggerSentence(run.trigger)} ·{' '}
          {run.startedAt ? (
            <time dateTime={new Date(run.startedAt).toISOString()}>
              {new Date(run.startedAt).toLocaleString()}
            </time>
          ) : (
            'never started'
          )}
        </p>
        {run.cancellationRequested && isRunLive(run.status) ? (
          <p className="rn-muted">
            Cancellation asked for. It takes effect at the run’s next step.
          </p>
        ) : null}
        {mayCancel && isRunLive(run.status) && !run.cancellationRequested ? (
          <div>
            <Button variant="secondary" disabled={cancel.isPending} onClick={() => cancel.mutate()}>
              Cancel this run
            </Button>
          </div>
        ) : null}
      </header>

      {actionError ? (
        <LoadFailure
          error={actionError}
          message="That did not go through. Nothing has changed."
          refusedHeading="You may not do that"
        />
      ) : null}

      {run.errors.length > 0 ? (
        <section className="rn-card card-record">
          <h2 className="rn-card-title">Why it ended this way</h2>
          <ul className="rn-list">
            {run.errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* ===== WHAT IT DID =================================================== */}
      <section className={prepared ? 'rn-card agent-prepared' : 'rn-card card-record'}>
        {prepared ? <p className="agent-prepared-label">Prepared — not sent</p> : null}
        <h2 className="rn-card-title">{prepared ? 'What it would do' : 'What it did'}</h2>
        {run.actions.length === 0 ? (
          <p className="rn-muted">
            This run took no actions. Nothing in your records was touched by it.
          </p>
        ) : (
          <ul className="rn-list">
            {run.actions.map((action) => {
              const note = actionOutcomeNote(action.executionResult);
              return (
                <li key={action.id} className="rn-col" style={{ gap: 'var(--space-8)' }}>
                  <div className="rn-row" style={{ gap: 'var(--space-12)', alignItems: 'center' }}>
                    <span className="rn-strong">{action.operation}</span>
                    <Pill variant={actionOutcomePill(action.executionResult)}>
                      {actionOutcomeSentence(action.executionResult)}
                    </Pill>
                  </div>
                  <span className="rn-muted">
                    {action.targetCount === 1 ? 'One record' : `${action.targetCount} records`}
                    {action.permissionChecked
                      ? ` · checked ${action.permissionChecked} before acting`
                      : ''}
                  </span>
                  {action.refusalReason ? (
                    <span className="rn-muted">Refused: {action.refusalReason}</span>
                  ) : null}
                  {note ? <span>{note}</span> : null}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ===== THE DECISION ==================================================
          The moment of commitment stays solid: the glass above marks what is
          prepared; these buttons are the operational act. */}
      {pendingApproval ? (
        <section className="rn-card agent-approval-bar card-record">
          <p className="rn-muted agent-approval-note">
            {pendingApproval.reason ?? 'Approving executes the prepared actions.'}
          </p>
          {authorityOpen(approve) ? (
            <>
              <Input
                label="Why"
                hint="Required, and recorded with your decision against this exact step."
                value={reason}
                maxLength={1000}
                onChange={(e) => setReason(e.target.value)}
              />
              <div className="agent-approval-actions">
                <button
                  type="button"
                  className="rn-btn"
                  disabled={decisionBlocked}
                  onClick={() =>
                    decide.mutate({ approvalId: pendingApproval.id, decision: 'GRANTED' })
                  }
                >
                  Approve
                </button>
                <button
                  type="button"
                  className="rn-btn-quiet"
                  disabled={decisionBlocked}
                  onClick={() =>
                    decide.mutate({ approvalId: pendingApproval.id, decision: 'DENIED' })
                  }
                >
                  Deny — nothing is sent
                </button>
              </div>
            </>
          ) : approveSentence ? (
            <p className="rn-muted" data-approve-gate={approve.kind}>
              {approveSentence}
            </p>
          ) : null}
        </section>
      ) : null}

      {/* ===== APPROVALS ===================================================== */}
      {run.approvals.length > 0 ? (
        <section className="rn-card card-record">
          <h2 className="rn-card-title">Approvals</h2>
          <ul className="rn-list">
            {run.approvals.map((approval) => (
              <li key={approval.id} className="rn-col" style={{ gap: 'var(--space-8)' }}>
                <span>
                  {approval.status === 'PENDING'
                    ? 'Waiting for a decision'
                    : approval.status === 'GRANTED'
                      ? 'Approved'
                      : 'Denied'}
                </span>
                {approval.reason ? <span className="rn-muted">{approval.reason}</span> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* ===== THE TIMELINE ================================================== */}
      {/* The recorded log of the run — committed audit, so a solid island. */}
      <section className="rn-card card-record">
        <h2 className="rn-card-title">Step by step</h2>
        {run.timeline.length === 0 ? (
          <p className="rn-muted">Nothing was recorded for this run.</p>
        ) : (
          <ol className="rn-list">
            {run.timeline.map((event) => (
              <li key={event.seq} className="rn-col" style={{ gap: 'var(--space-8)' }}>
                <span>{event.summary}</span>
                <time className="rn-muted" dateTime={new Date(event.at).toISOString()}>
                  {new Date(event.at).toLocaleString()}
                </time>
              </li>
            ))}
          </ol>
        )}
      </section>
    </article>
  );
}
